import axios from "axios";
import { put, takeLatest, takeLeading, takeEvery, select } from "redux-saga/effects";
import { base_url } from "../../components/config";
import {
  SIGNUP_ACTION,
  SIGNUP_ACTION_LOADER,
  LOGIN_ACTION,
  LOGIN_ACTION_LOADER,
  CHECK_AUTH_ACTION,
  LOGOUT_ACTION,
  UPDATE_CUSTOMER_ACTION,
  UPDATE_CUSTOMER_ACTION_LOADER,
  ADD_ADDRESS_ACTION,
  ADD_ADDRESS_FAILURE,
  ADD_ADDRESS_SUCCESS,
  DELETE_ADDRESS_ACTION,
  DELETE_ADDRESS_SUCCESS,
  DELETE_ADDRESS_FAILURE,
  UPDATE_ADDRESS_ACTION,
  UPDATE_ADDRESS_SUCCESS,
  UPDATE_ADDRESS_FAILURE,
  RESET_PASSWORD_ACTION,
  FORGOT_PASSWORD_ACTION,
  RESET_PASSWORD_ACTION_LOADER,
  FORGOT_PASSWORD_ACTION_LOADER
} from "../type";


const normalizeError = (msg) => {

  if (!msg) return null;
  if (typeof msg !== 'string') return String(msg);

  if (msg.includes('Customer->lastname')) {
    return 'Last name is not valid';
  }

  if (msg.includes('Customer->firstname')) {
    return 'First name is not valid';
  }

  if (msg.includes('Customer->email')) {
    return 'Please enter a valid email address';
  }
  return msg;
};


// Signup Saga
function* signupUser(payload) {

  const labels = yield select(state => state.home.common_data?.lang_labels);
  const __ = (key) => (labels && labels[key]) ? labels[key] : key;
  try {
    yield put({ type: SIGNUP_ACTION_LOADER, loader: true });

    const data = payload.data;
    const response = yield axios.post(
      `${base_url}ps_authapi/api/?action=customer_signup`,
      data,
      { withCredentials: true }
    );

    if (response.status === 200 && response.data.success) {
      yield put({
        type: SIGNUP_ACTION,
        success: true,
        isAuthenticated: true,
        user: response.data.customer,
        customer_id: response.data.customer_id,
        message: __('Account created successfully!')
      });
    }
    else {
      const rawMessage = __(response.data.message);
      const friendlyMessage = normalizeError(rawMessage);
      
      
      yield put({
        type: SIGNUP_ACTION,
        success: false,
        error: friendlyMessage,
        message: friendlyMessage
      });
    }
  } catch (error) {
    const rawMessage =
      __(error.response?.data?.message) || __(error.message);

    const normalizedMessage = normalizeError(rawMessage);

    const finalMessage = normalizedMessage || rawMessage || __('Network error. Please try again.');

    yield put({
      type: SIGNUP_ACTION,
      success: false,
      error: finalMessage,
      message: finalMessage
    });
  } finally {
    yield put({ type: SIGNUP_ACTION_LOADER, loader: false });
  }
}

export function* watchSignup() {
  yield takeLeading("CSignup_sync", signupUser);
}

// Login Saga
function* loginUser(payload) {
  const labels = yield select(state => state.home.common_data?.lang_labels);
  const __ = (key) => (labels && labels[key]) ? labels[key] : key;
  
  try {
    yield put({ type: LOGIN_ACTION_LOADER, loader: true });

    const data = payload.data;
    const response = yield axios.post(
      `${base_url}ps_authapi/api/?action=customer_login`,
      data,
      { withCredentials: true }
    );

    if (response.status === 200 && response.data.success) {
      yield put({
        type: LOGIN_ACTION,
        success: true,
        user: response.data.customer,
        customer_id: response.data.customer_id,
        message: __(response.data.message) || __('Login successful!')
      });
      
      // ✅ FORCE REFRESH CART AND CHECKOUT AFTER LOGIN
      yield put({ type: 'CCartList_sync' });
      yield put({ type: 'CCheckAuth_sync' });
      yield put({ type: 'CcommonContent_sync' });
      yield put({ type: 'CCheckoutPage_sync', payload: {} });
      
    } else {
      yield put({
        type: LOGIN_ACTION,
        success: false,
        error: __(response.data.message) || __('Invalid credentials'),
        message: __(response.data.message) || __('Invalid credentials')
      });
    }
  } catch (error) {
    yield put({
      type: LOGIN_ACTION,
      success: false,
      error: __(error.response?.data?.message) || __(error.message),
      message: __('Login failed. Please try again.')
    });
  } finally {
    yield put({ type: LOGIN_ACTION_LOADER, loader: false });
  }
}

export function* watchLogin() {
  yield takeLeading("CLogin_sync", loginUser);
}

function* updateCustomer(payload) {

  const labels = yield select(state => state.home.common_data?.lang_labels);
  const __ = (key) => (labels && labels[key]) ? labels[key] : key;
  try {
    yield put({ type: UPDATE_CUSTOMER_ACTION_LOADER, loader: true });

    const data = payload.data;

    const response = yield axios.post(
      `${base_url}ps_authapi/api/?action=customer_update`,
      data,
      { withCredentials: true }
    );

    if (response.status === 200 && response.data.success) {
      // Check if an address was added
      let message = __(response.data.message) || __('Profile updated successfully');
      if (response.data.customer?.added_address) {
        message = __('Address added successfully');
      }

      yield put({
        type: UPDATE_CUSTOMER_ACTION,
        success: true,
        message,
        user: response.data.customer
      });

      yield put({ type: 'CcommonContent_sync' });

    } else {
      const rawMessage = __(response.data.message);
      const normalized = normalizeError(rawMessage);
      const finalMessage = normalized || rawMessage || __('Failed to update');

      yield put({
        type: UPDATE_CUSTOMER_ACTION,
        success: false,
        error: finalMessage,
        message: finalMessage
      });
    }

  } catch (error) {
    const rawMessage = __(error.response?.data?.message) || __(error.message);
    const normalized = normalizeError(rawMessage);
    const finalMessage = normalized || rawMessage || __('Network error. Please try again.');

    yield put({
      type: UPDATE_CUSTOMER_ACTION,
      success: false,
      error: finalMessage,
      message: finalMessage
    });
  } finally {
    yield put({ type: UPDATE_CUSTOMER_ACTION_LOADER, loader: false });
  }
}

export function* watchUpdateCustomer() {
  yield takeLeading('CUpdateCustomer_sync', updateCustomer);
}

function* checkAuth() {

  try {
    
    const response = yield axios.get(
      `${base_url}ps_authapi/api/?action=customer_info`,
      { withCredentials: true }
    );
    
    if (response.data.success && response.data.customer) {
      yield put({
        type: CHECK_AUTH_ACTION,
        isAuthenticated: true,
        user: response.data.customer,
        customer_id: response.data.customer.id
      });
    } else {
      // Expliciet "niet ingelogd" antwoord van de backend
      yield put({
        type: CHECK_AUTH_ACTION,
        isAuthenticated: false,
        user: null
      });
    }
  } catch (error) {
    // Alleen uitloggen bij een expliciete 401/403 (backend: "Not authenticated").
    // Netwerkfouten / 5xx: huidige auth-state behouden.
    const status = error.response?.status;
    if (status === 401 || status === 403) {
      yield put({
        type: CHECK_AUTH_ACTION,
        isAuthenticated: false,
        user: null
      });
    }
  }
}

export function* watchCheckAuth() {
  
  yield takeLatest("CCheckAuth_sync", checkAuth);
}

// Logout Saga
function* logoutUser() {
  try {

    yield axios.post(
      `${base_url}ps_authapi/api/?action=customer_logout`,
      {},
      { withCredentials: true }
    );

    yield put({ type: LOGOUT_ACTION });
    yield put({ type: 'CcommonContent_sync' });
  } catch (error) {
    // Even if API fails, logout locally
    yield put({ type: LOGOUT_ACTION });
  }
}

export function* watchLogout() {
  yield takeLeading("CLogout_sync", logoutUser);
}

// Add Address
// In your sagas file, update the addAddress function:

// function* addAddress({ data }) {
//   const labels = yield select(state => state.home.common_data?.lang_labels);
//   const __ = (key) => (labels && labels[key]) ? labels[key] : key;
  
//   // Get current user's newsletter value from Redux state
//   const currentUser = yield select(state => state.auth.user);
//   const newsletterValue = currentUser?.newsletter || 0;
  
//   try {
//     yield put({ type: ADD_ADDRESS_ACTION });
//     const response = yield axios.post(
//       `${base_url}ps_authapi/api/?action=customer_update`,
//       { 
//         address: data,
//         newsletter: newsletterValue  // ← Add the current newsletter value
//       },
//       { withCredentials: true }
//     );

//     if (response.data.success) {
//       yield put({
//         type: ADD_ADDRESS_SUCCESS,
//         message: __('Address added successfully'),
//         user: response.data.customer
//       });
//       yield put({ type: 'CCheckAuth_sync' });
//     } else {
//       yield put({
//         type: ADD_ADDRESS_FAILURE,
//         message: normalizeError(__(response.data.message))
//       });
//     }
//   } catch (error) {
//     yield put({
//       type: ADD_ADDRESS_FAILURE,
//       message: normalizeError(__(error.response?.data?.message) || __(error.message))
//     });
//   }
// }
function* addAddress({ data }) {
  const labels = yield select(state => state.home.common_data?.lang_labels);
  const __ = (key) => (labels && labels[key]) ? labels[key] : key;
  
  // Get current user's newsletter value from Redux state
  const currentUser = yield select(state => state.auth.user);
  const newsletterValue = currentUser?.newsletter || 0;
  const isAuthenticated = yield select(state => state.auth.isAuthenticated);
  
  try {
    yield put({ type: ADD_ADDRESS_ACTION });
    
    // ✅ Choose endpoint based on authentication status
    const endpoint = isAuthenticated 
      ? `${base_url}ps_authapi/api/?action=customer_update`
      : `${base_url}ps_checkoutapi/api/?action=address_save`;
    
    // ✅ Different payload for guests vs logged-in users
    const payload = isAuthenticated 
      ? { address: data, newsletter: newsletterValue }
      : { ...data }; // For guests, send address data directly
    
    const response = yield axios.post(
      endpoint,
      payload,
      { withCredentials: true }
    );

    if (response.data.success) {
      yield put({
        type: ADD_ADDRESS_SUCCESS,
        message: __('Address added successfully'),
        user: response.data.customer,
        address: response.data.address
      });
      
      // ✅ Refresh auth state for logged-in users
      if (isAuthenticated) {
        yield put({ type: 'CCheckAuth_sync' });
      }
      
      // ✅ Refresh common content to get updated addresses for guests
      yield put({ type: "CcommonContent_sync", payload: {} });
      yield put({ type: "CCartList_sync" });
      
    } else {
      yield put({
        type: ADD_ADDRESS_FAILURE,
        message: normalizeError(__(response.data.message))
      });
    }
  } catch (error) {
    yield put({
      type: ADD_ADDRESS_FAILURE,
      message: normalizeError(__(error.response?.data?.message) || __(error.message))
    });
  }
}
// Update Address
// function* updateAddress({ data }) {

//   const labels = yield select(state => state.home.common_data?.lang_labels);
//   const __ = (key) => (labels && labels[key]) ? labels[key] : key;
//   try {
//     yield put({ type: UPDATE_ADDRESS_ACTION });
//     const response = yield axios.post(
//       `${base_url}ps_authapi/api/?action=customer_update_address`,
//       { address: data, id: data.id },
//       { withCredentials: true }
//     );

//     if (response.data.success) {
//       yield put({
//         type: UPDATE_ADDRESS_SUCCESS,
//         message: __('Address updated successfully'),
//         user: response.data.customer
//       });

//       yield put({ type: 'CCheckAuth_sync' });
//     } else {
//       yield put({
//         type: UPDATE_ADDRESS_FAILURE,
//         message: normalizeError(__(response.data.message))
//       });
//     }
//   } catch (error) {
//     yield put({
//       type: UPDATE_ADDRESS_FAILURE,
//       message: normalizeError(__(error.response?.data?.message) || __(error.message))
//     });
//   }
// }
function* updateAddress({ data }) {
  const labels = yield select(state => state.home.common_data?.lang_labels);
  const __ = (key) => (labels && labels[key]) ? labels[key] : key;
  const isAuthenticated = yield select(state => state.auth.isAuthenticated);
  
  try {
    yield put({ type: UPDATE_ADDRESS_ACTION });
    
    // ✅ For guests, use address_save (handles both create and update)
    // ✅ For logged-in users, use customer_update_address
    const endpoint = isAuthenticated 
      ? `${base_url}ps_authapi/api/?action=customer_update_address`
      : `${base_url}ps_checkoutapi/api/?action=address_save`;
    
    const payload = isAuthenticated 
      ? { address: data, id: data.id }
      : { 
          id_address: data.id,  // For update
          alias: data.alias,
          firstname: data.firstname,
          lastname: data.lastname,
          dni: data.dni,
          address1: data.address1,
          address2: data.address2,
          city: data.city,
          postcode: data.postcode,
          id_country: data.id_country,
          id_state: data.id_state,
          phone: data.phone,
          phone_mobile: data.phone_mobile,
          company: data.company,
          vat_number: data.vat_number
        };
    
    const response = yield axios.post(
      endpoint,
      payload,
      { withCredentials: true }
    );

    if (response.data.success) {
      yield put({
        type: UPDATE_ADDRESS_SUCCESS,
        message: __('Address updated successfully'),
        user: response.data.customer,
        address: response.data.address
      });
      
      // Refresh data
      yield put({ type: 'CCheckAuth_sync' });
      yield put({ type: "CcommonContent_sync", payload: {} });
      yield put({ type: "CCartList_sync" });
    } else {
      yield put({
        type: UPDATE_ADDRESS_FAILURE,
        message: normalizeError(__(response.data.message))
      });
    }
  } catch (error) {
    yield put({
      type: UPDATE_ADDRESS_FAILURE,
      message: normalizeError(__(error.response?.data?.message) || __(error.message))
    });
  }
}
// Delete Address
function* deleteAddress({ id }) {

  const labels = yield select(state => state.home.common_data?.lang_labels);
  const __ = (key) => (labels && labels[key]) ? labels[key] : key;
  try {
    yield put({ type: DELETE_ADDRESS_ACTION });
    const response = yield axios.post(
      `${base_url}ps_authapi/api/?action=customer_delete_address`,
      { id },
      { withCredentials: true }
    );

    if (response.data.success) {
      yield put({
        type: DELETE_ADDRESS_SUCCESS,
        payload: id,
        message: __('Address deleted successfully'),
      });
      yield put({ type: 'CCheckAuth_sync' });

    } else {
      yield put({
        type: DELETE_ADDRESS_FAILURE,
        message: normalizeError(__(response.data.message))
      });
    }
  } catch (error) {
    yield put({
      type: DELETE_ADDRESS_FAILURE,
      message: normalizeError(__(error.response?.data?.message) || __(error.message))
    });
  }
}


// Forgot Password Saga
function* forgotPassword(payload) {
    try {
        yield put({
            type: FORGOT_PASSWORD_ACTION_LOADER,
            loader: true
        });

        const data = payload.data;

        const response = yield axios.post(
            `${base_url}ps_authapi/api/?action=forgot_password`,
            data,
            { withCredentials: true }
        );

        if (response.status === 200 && response.data.success) {

            yield put({
                type: FORGOT_PASSWORD_ACTION,
                success: true,
                message: response.data.message || 'Password reset link has been sent.'
            });

        } else {

            const rawMessage =
                response.data.message || 'Unable to send password reset email.';

            const friendlyMessage = normalizeError(rawMessage);

            yield put({
                type: FORGOT_PASSWORD_ACTION,
                success: false,
                error: friendlyMessage,
                message: friendlyMessage
            });
        }

    } catch (error) {

        const rawMessage =
            error.response?.data?.message || error.message;

        const normalizedMessage = normalizeError(rawMessage);

        const finalMessage =
            normalizedMessage ||
            rawMessage ||
            'Network error. Please try again.';

        yield put({
            type: FORGOT_PASSWORD_ACTION,
            success: false,
            error: finalMessage,
            message: finalMessage
        });

    } finally {

        yield put({
            type: FORGOT_PASSWORD_ACTION_LOADER,
            loader: false
        });
    }
}

// Reset Password Saga
function* resetPassword(payload) {
    try {

        yield put({
            type: RESET_PASSWORD_ACTION_LOADER,
            loader: true
        });

        const data = payload.data;

        const response = yield axios.post(
            `${base_url}ps_authapi/api/?action=reset_password`,
            data,
            { withCredentials: true }
        );

        if (response.status === 200 && response.data.success) {

            yield put({
                type: RESET_PASSWORD_ACTION,
                success: true,
                message: response.data.message || 'Password reset successfully.'
            });

        } else {

            const rawMessage =
                response.data.message || 'Unable to reset password.';

            const friendlyMessage = normalizeError(rawMessage);

            yield put({
                type: RESET_PASSWORD_ACTION,
                success: false,
                error: friendlyMessage,
                message: friendlyMessage
            });
        }

    } catch (error) {

        const rawMessage =
            error.response?.data?.message || error.message;

        const normalizedMessage = normalizeError(rawMessage);

        const finalMessage =
            normalizedMessage ||
            rawMessage ||
            'Network error. Please try again.';

        yield put({
            type: RESET_PASSWORD_ACTION,
            success: false,
            error: finalMessage,
            message: finalMessage
        });

    } finally {

        yield put({
            type: RESET_PASSWORD_ACTION_LOADER,
            loader: false
        });
    }
}

export function* watchResetPassword() {
    yield takeLeading(
        "CResetPassword_sync",
        resetPassword
    );
}
export function* watchForgotPassword() {
    yield takeLeading(
        "CForgotPassword_sync",
        forgotPassword
    );
}
// Watchers
export function* watchAddAddress() {
  yield takeLeading("CAddAddress_sync", addAddress);
}

export function* watchUpdateAddress() {
  yield takeLeading("CUpdateAddress_sync", updateAddress);
}

export function* watchDeleteAddress() {
  // takeEvery: verschillende adressen snel na elkaar verwijderen mag niet wegvallen
  yield takeEvery("CDeleteAddress_sync", deleteAddress);
}
